<template>
  <div class="team-gpx-upload">
    <label class="file-label" v-if="!busy">
      <input
        type="file"
        accept=".gpx,application/gpx+xml,application/xml"
        multiple
        @change="onFile"
      />
      <span class="file-cta">
        {{ files.length ? 'Adicionar mais arquivos .gpx' : 'Escolha os arquivos .gpx' }}
      </span>
    </label>

    <p class="hint" v-if="!files.length">
      Você pode selecionar vários arquivos de uma vez. Tamanho máximo: 100 MB cada.
      <router-link to="/gpx-tutorial">Como exportar do Strava ou Garmin?</router-link>
    </p>

    <ul v-if="files.length" class="file-list">
      <li v-for="(f, i) in files" :key="f.name + '-' + f.size + '-' + i">
        <span class="fname">{{ f.name }}</span>
        <span class="size">{{ humanSize(f.size) }}</span>
        <button
          v-if="!busy"
          class="link remove"
          type="button"
          @click="removeFile(i)"
          title="Remover"
        >✕</button>
      </li>
    </ul>

    <div class="actions" v-if="files.length">
      <button
        class="btn btn-primary"
        type="button"
        :disabled="busy"
        @click="startUpload"
      >
        {{ busy
          ? ('Enviando ' + (currentIndex + 1) + ' de ' + files.length + '...')
          : ('Enviar ' + files.length + (files.length > 1 ? ' arquivos' : ' arquivo')) }}
      </button>
      <button
        v-if="!busy"
        class="link"
        type="button"
        @click="clearAll"
      >limpar</button>
    </div>

    <div v-if="busy" class="progress-wrap">
      <div class="progress-bar" :style="{ width: percent + '%' }"></div>
      <span class="progress-label">{{ percent }}%</span>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="success" class="alert alert-success">{{ success }}</div>
  </div>
</template>

<script>
import axios from 'axios';
import api from '@/api';

export default {
  name: 'team-gpx-upload',

  props: {
    teamId: { type: [Number, String], required: true },
  },

  data() {
    return {
      files: [],
      currentIndex: 0,
      percent: 0,
      busy: false,
      error: null,
      success: null,
    };
  },

  methods: {
    onFile(ev) {
      this.error = null;
      this.success = null;
      const picked = Array.from((ev.target && ev.target.files) || []);
      const rejected = [];
      for (const f of picked) {
        if (!/\.gpx$/i.test(f.name)) { rejected.push(f.name + ' (não é .gpx)'); continue; }
        if (f.size > 100 * 1024 * 1024) { rejected.push(f.name + ' (excede 100 MB)'); continue; }
        // Skip duplicates already in the list (same name + size).
        const dup = this.files.some((x) => x.name === f.name && x.size === f.size);
        if (!dup) this.files.push(f);
      }
      if (rejected.length) {
        this.error = 'Ignorados: ' + rejected.join(', ');
      }
      // Reset the input so re-selecting the same file fires change again.
      if (ev.target) ev.target.value = '';
    },

    removeFile(i) {
      this.files.splice(i, 1);
    },

    clearAll() {
      this.files = [];
      this.currentIndex = 0;
      this.percent = 0;
      this.error = null;
      this.success = null;
    },

    humanSize(n) {
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      return (n / 1024 / 1024).toFixed(1) + ' MB';
    },

    async uploadOne(file) {
      // 1) Initiate — get partUrls.
      const init = await api.postJSONAsync('gpxInitiateUpload', {
        teamId: Number(this.teamId),
        filename: file.name,
        sizeBytes: file.size,
        contentType: 'application/gpx+xml',
      });
      if (init.error) throw new Error(init.message || 'Falha ao iniciar upload');

      // 2) Upload parts sequentially.
      const partSize = init.partSizeBytes;
      const parts = [];
      let totalUploaded = 0;
      for (const p of init.partUrls) {
        const start = (p.partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);

        const res = await axios.put(p.url, chunk, {
          headers: { 'Content-Type': 'application/gpx+xml' },
          onUploadProgress: (e) => {
            if (!e.total) return;
            this.percent = Math.min(
              99,
              Math.round(((totalUploaded + e.loaded) / file.size) * 100)
            );
          },
          transformRequest: [(d) => d],
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        const etag = res.headers && (res.headers.etag || res.headers.ETag);
        if (!etag) throw new Error('ETag ausente na parte ' + p.partNumber);
        parts.push({ PartNumber: p.partNumber, ETag: etag });
        totalUploaded += (end - start);
      }

      // 3) Finalize.
      const fin = await api.postJSONAsync('gpxFinalizeUpload', {
        key: init.key,
        uploadId: init.uploadId,
        parts,
        teamId: Number(this.teamId),
        originalFilename: file.name,
        sizeBytes: file.size,
      });
      if (fin.error) throw new Error(fin.message || 'Falha ao finalizar upload');
      return fin;
    },

    async startUpload() {
      if (!this.files.length || this.busy) return;
      this.busy = true;
      this.error = null;
      this.success = null;

      const total = this.files.length;
      const failed = [];
      let sent = 0;

      for (let i = 0; i < total; i++) {
        this.currentIndex = i;
        this.percent = 0;
        try {
          const fin = await this.uploadOne(this.files[i]);
          sent += 1;
          this.$emit('uploaded', fin);
        } catch (e) {
          failed.push(this.files[i].name + ' — ' + (e && (e.message || e.toString())));
        }
      }

      this.busy = false;
      this.percent = 100;

      if (failed.length) {
        this.error = 'Falha em ' + failed.length + ' de ' + total + ': ' + failed.join('; ');
      }
      if (sent > 0) {
        this.success = sent + (sent > 1 ? ' arquivos enviados.' : ' arquivo enviado.')
          + ' Estamos processando.';
      }
      // Keep only the files that failed so the user can retry them.
      this.files = this.files.filter((f) =>
        failed.some((msg) => msg.indexOf(f.name + ' — ') === 0)
      );
      this.currentIndex = 0;
    },
  },
};
</script>

<style scoped lang="stylus">
.team-gpx-upload
  padding: 1em
  border: 1px dashed rgba(0,0,0,0.15)
  border-radius: 8px
  background: #fafbfc

.file-label
  display: inline-block
  cursor: pointer

.file-cta
  display: inline-block
  padding: 0.6em 1.2em
  background: #17a2b8
  color: white
  border-radius: 6px

.file-label input[type=file]
  display: none

.hint
  color: #666
  font-size: 0.9em
  margin: 0.5em 0 0

.file-list
  list-style: none
  padding: 0
  margin: 0.9em 0 0.4em

.file-list li
  display: flex
  align-items: center
  gap: 0.7em
  padding: 0.45em 0
  border-bottom: 1px solid rgba(0,0,0,0.06)

.fname
  font-weight: 600
  color: #333
  word-break: break-all

.size
  color: #888
  font-size: 0.9em
  margin-left: auto

.remove
  color: #c0392b
  font-size: 1em
  line-height: 1

.link
  background: none
  border: none
  color: #17a2b8
  cursor: pointer
  padding: 0

.actions
  margin-top: 0.9em
  display: flex
  align-items: center
  gap: 1em

.progress-wrap
  margin-top: 0.8em
  position: relative
  height: 22px
  background: rgba(0,0,0,0.06)
  border-radius: 4px
  overflow: hidden

.progress-bar
  height: 100%
  background: #17a2b8
  transition: width 0.2s

.progress-label
  position: absolute
  top: 0
  left: 50%
  transform: translateX(-50%)
  font-size: 0.85em
  line-height: 22px
  color: white
  text-shadow: 0 0 2px rgba(0,0,0,0.4)

.alert
  margin-top: 0.8em
  padding: 0.6em 0.8em
  border-radius: 4px

.alert-danger
  background: #fde2e1
  color: #842029

.alert-success
  background: #d1e7dd
  color: #0f5132
</style>
